import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import useCachedPriceLevelsDictionaryController from "../../../../../../hooks/use-price-level-dictionary";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import {Box, Card, Skeleton, Typography, useTheme} from "@mui/material";
import IconButton from "../../../../../base/button/icon_button";
import {ChevronLeft, ChevronRight, DeleteOutlined, Remove} from "@mui/icons-material";
import {ResponsiveSlider} from "../../../../../base/slider/slider";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import Divider from "@mui/material/Divider";
import CustomerAutocomplete from "../../../../../text-fields/autocomplete/customer-autocomplete";
import {Alert} from "@mui/lab";

const PricesSection = ({partId, partNo, cache, updateCache}) => {
    const theme = useTheme();
    const [customer, setCustomer] = useState(null);
    const [prices, setPrices] = useState([]);
    const [pricesLoading, setPricesLoading] = useState(!cache.prices);
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();
    const [loadingCurrentCustomerPrice, setLoadingCurrentCustomerPrice] = useState(false);
    const [currentCustomerPriceAndPriceLevel, setCurrentCustomerPriceAndPriceLevel] = useState({});
    const [compareCustomers, setCompareCustomers] = useState([]);
    const [addingCompareCustomer, setAddingCompareCustomer] = useState(false);

    /** @type {React.MutableRefObject<import('react-slick').default>}*/
    const priceLevelSliderRef = useRef();
    const isMounted = useIsMounted();

    const customerId = useMemo(() => customer?.id, [customer]);

    /**
     * Fetches the part's prices.
     */
    const getPartPrices = useCallback(async () => {
        setPricesLoading(true);
        const [response, priceLevelDict] = await Promise.all([
            Api.getPartLivePriceLevels(partId),
            getPriceLevelDictionary(),
        ])
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const prices = response?.data
                    ?.sort((a, b) => a.priceLevel.levelIndex - b.priceLevel.levelIndex)
                    ?.map(e => ({
                        ...e,
                        priceLevel: {
                            ...e.priceLevel,
                            title: priceLevelDict?.find(p => p.priceLevelId === e.priceLevel.id)?.title ?? e.priceLevel?.title,
                        },
                    }))
                ?? []
            setPrices(prices)
            updateCache('prices', prices);
        }
        setPricesLoading(false);
    }, [getPriceLevelDictionary, isMounted, partId, updateCache])

    /**
     * Fetches the part prices on mount.
     */
    useLayoutEffect(() => {
        if (cache.prices) {
            setPrices(cache.prices);
            setPricesLoading(false);
            return;
        }
        void getPartPrices();
    }, [cache.prices, getPartPrices]);

    /**
     * Fetches the price of the part for the selected customer.
     * @type {(function(): Promise<void>)}
     */
    const getCurrentCustomerPrice = useCallback(async () => {
        setLoadingCurrentCustomerPrice(true);
        const response = await Api.getPriceForCustomer({
            partNumbers: [partNo],
            customerId: customerId,
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const data = response.data?.find(e => e.partNo === partNo)
            setCurrentCustomerPriceAndPriceLevel({
                price: data?.price ?? 0,
                priceLevelTitle: prices?.find(e => e.priceLevel.id === data?.priceLevelId)?.priceLevel?.title ?? '--',
            });
        } else {
            setCurrentCustomerPriceAndPriceLevel({});
        }
        setLoadingCurrentCustomerPrice(false);
    }, [isMounted, partNo, prices, customerId])

    /**
     * With each change in the customer:
     * - Resets the compare customers array
     */
    useEffect(() => {
        setCompareCustomers([]);
        if (!customerId)
            return;
        void getCurrentCustomerPrice();
    }, [customerId, getCurrentCustomerPrice]);

    /**
     * With each change in the user id or the part number:
     * - Sets the loading of the current customer price to true
     */
    useLayoutEffect(() => {
        if (customerId) {
            setLoadingCurrentCustomerPrice(true);
        }
    }, [customerId, partNo]);

    /**
     * Fetches the price of the part for the selected customer and then adds the customer to the compare customers
     * @type {(function(*, any): Promise<void>)}
     */
    const getCompareCustomerPrice = useCallback(async (_, customer) => {
        setAddingCompareCustomer(true);
        const response = await Api.getPriceForCustomer({
            partNumbers: [partNo],
            customerId: customer.id,
        });
        if (!isMounted())
            return;
        setAddingCompareCustomer(false);
        if (response?.resultFlag) {
            const data = response.data?.find(e => e.partNo === partNo)
            setCompareCustomers(prevState => [
                ...prevState,
                {
                    customerId: customer?.id,
                    customerName: customer?.name,
                    price: data?.price ?? 0,
                    priceLevelTitle: prices?.find(e => e.priceLevel.id === data?.priceLevelId)?.priceLevel?.title ?? '--',
                }
            ]);
        }
    }, [partNo, prices, isMounted])

    /**
     * Sets the customer in the url
     * @type {(function(*, any): void)}
     */
    const changeCustomer = useCallback((_, customer) => {
        setCustomer(customer);
    }, [])


    /**
     * Removes the customer from the url and replaces the url with the new url
     * @type {(function(): void)}
     */
    const removeCustomer = useCallback(() => {
        setCustomer(null);
    }, [])


    /**
     * Slides the price level slider left or right based on the received offset
     * @param {number} offset
     */
    const slidePriceLevels = useCallback((offset) => {
        if (offset > 0) {
            priceLevelSliderRef.current.slickNext();
            return;
        }
        priceLevelSliderRef.current.slickPrev();
    }, [])

    const applicablePrices = useMemo(() => {
        const nonZeroPrices = prices?.filter(info => (info?.price ?? 0) !== 0) ?? [];
        if (nonZeroPrices.length === 0) {
            return prices ?? [];
        }
        return nonZeroPrices ?? [];
    }, [prices])

    const allPricesZero = useMemo(() => prices?.every(info => (info?.price ?? 0) === 0), [prices])

    return (
        <Box sx={{paddingInline: 2, display: 'flex', flexDirection: 'column'}}>
            {
                pricesLoading &&
                <Box sx={{maxWidth: '100%', overflowX: 'auto'}}>
                    <Box sx={{width: `${800}px !important`}} className='loading part-prices'>
                        <div/>
                        <div/>
                        <div/>
                        <div/>
                        <div/>
                    </Box>
                </Box>
            }
            {
                !pricesLoading && !customer &&
                <>
                    {
                        applicablePrices?.length > 0 ? (
                                allPricesZero
                                    ? <div className={'look-up-single-price-levels'}>
                                        <Alert variant={'filled'} className='my-2' severity={'warning'}>
                                            <Typography variant={'body2'} fontWeight={500}>
                                                This part has prices, but all of them are zero.
                                            </Typography>
                                        </Alert>
                                    </div>
                                    : <div id={'price-levels-id'} className={'look-up-single-price-levels'}>
                                        {
                                            applicablePrices?.length > 1 &&
                                            <IconButton
                                                color={"primary"}
                                                className={'slider-arrow prev'}
                                                onPressed={() => slidePriceLevels(-1)}
                                            >
                                                <ChevronLeft className={'icon small'}/>
                                            </IconButton>
                                        }
                                        {
                                            applicablePrices?.length === 1 &&
                                            <Card variant={'outlined'}
                                                  sx={{padding: 2, backgroundColor: 'background.default', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
                                                <Typography variant={'body2'} fontWeight={500}>
                                                    {applicablePrices?.[0].priceLevel?.title ?? 'Price'}:
                                                </Typography>
                                                <Typography variant={'body1'} fontWeight={600}>
                                                    {formatMoney(applicablePrices?.[0]?.price ?? 0)}
                                                </Typography>
                                            </Card>
                                        }
                                        {
                                            applicablePrices?.length > 1 &&
                                            <ResponsiveSlider
                                                ref={priceLevelSliderRef}
                                                autoplay={false} infinite={false} fade={false} dots={false}
                                                centerMode={false} swipe={false} initialIndex={0}
                                                responsiveWidth={150}
                                                parentId={'price-levels-id'}
                                            >
                                                {
                                                    applicablePrices?.map((info) => (
                                                        <div
                                                            key={info.priceLevel?.id}
                                                            className='part-prices d-flex flex-column justify-content-start align-items-center px-2 p-1'
                                                        >
                                                            <p className='px-2 pt-1 text-xs font-weight-semi-bold secondary-color-1 text-left'>
                                                                {info.priceLevel?.title ?? ''}
                                                            </p>
                                                            <p className='py-2'>
                                                                {formatMoney(info?.price ?? 0)}
                                                            </p>
                                                        </div>
                                                    ))
                                                }
                                            </ResponsiveSlider>
                                        }
                                        {
                                            applicablePrices?.length > 1 &&
                                            <IconButton
                                                color={"primary"}
                                                className={'slider-arrow next'}
                                                onPressed={() => slidePriceLevels(1)}
                                            >
                                                <ChevronRight className={'icon small'}/>
                                            </IconButton>
                                        }
                                    </div>
                            ) :
                            <p className='text-color-3 my-2 text-sm text-center'>
                                This part does not have any prices.
                            </p>
                    }
                    <CustomerAutocomplete
                        label={'Customer'}
                        className={'mt-3'}
                        onChange={changeCustomer}
                        slotProps={{popper: {sx: {zIndex: theme.zIndex.modal + 2}}}}
                    />
                    <div className={'my-3'}/>
                </>
            }
            {
                !pricesLoading && !!customer &&
                <>
                    <Card
                        variant='outlined'
                        sx={{
                            background: 'background.tertiary',
                            display: 'flex',
                            alignItems: 'center',
                            padding: 2,
                            gap: 2,
                            marginBlock: 2,
                        }}
                    >
                        <IconButton onPressed={removeCustomer} color={'paper.error'}>
                            <DeleteOutlined className={'icon medium'}/>
                        </IconButton>
                        <Typography variant={'body2'} fontWeight={600}>
                            {customer?.name ?? ""}
                        </Typography>
                        {
                            loadingCurrentCustomerPrice &&
                            <Skeleton width={60} height={40} sx={{marginInlineStart: 'auto',}}/>
                        }
                        {
                            !loadingCurrentCustomerPrice &&
                            <Typography sx={{marginInlineStart: 'auto'}} variant={'body2'} fontWeight={500}>
                                {formatMoney(currentCustomerPriceAndPriceLevel.price)} ({currentCustomerPriceAndPriceLevel.priceLevelTitle})
                            </Typography>
                        }
                    </Card>
                    <Box sx={{position: 'relative', marginBlock: 2}}>
                        <Divider absolute sx={{
                            borderStyle: "dashed",
                            borderColor: 'text.disabled',
                            borderBottomWidth: 2,
                        }}/>
                        <Typography
                            variant={'body2'}
                            color={'tertiary.main'}
                            fontWeight={600}
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -65%)',
                                backgroundColor: 'background.paper',
                                paddingInline: 2,
                            }}
                        >
                            Compare Prices
                        </Typography>
                    </Box>
                    {
                        compareCustomers.map((compareCustomer) => (
                            <Card
                                key={compareCustomer.customerId}
                                variant='outlined'
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: 1,
                                    gap: 2,
                                    marginBlock: 1,
                                }}
                            >
                                <IconButton
                                    onPressed={() => setCompareCustomers(prevState => prevState.filter(e => e.customerId !== compareCustomer.customerId))}
                                    color={'paper.error'}
                                >
                                    <Remove className={'icon small'}/>
                                </IconButton>
                                <Typography variant={'body2'} fontWeight={500}>
                                    {compareCustomer?.customerName}
                                </Typography>
                                <Typography sx={{marginInlineStart: 'auto'}} variant={'body2'} fontWeight={500}>
                                    {formatMoney(compareCustomer?.price)} for ({compareCustomer?.priceLevelTitle}
                                </Typography>
                            </Card>
                        ))
                    }
                    {
                        addingCompareCustomer &&
                        <div className='loading small my-2'>
                            <div/>
                        </div>
                    }
                    <CustomerAutocomplete
                        label={'Customer'}
                        className={'mt-3'}
                        onChange={getCompareCustomerPrice}
                        value={null}
                        filterOptions={(options) => options.filter(option => customer.id !== option.id && !compareCustomers.some(e => e.customerId === option.id))}
                        slotProps={{popper: {sx: {zIndex: theme.zIndex.modal + 2}}}}
                    />
                    <div className={'my-3'}/>
                </>
            }
        </Box>
    );
}

export default PricesSection;